import {
  faChevronDown,
  faChevronRight,
  faFrown,
  faSpinner,
  faShield
} from '@fortawesome/pro-regular-svg-icons'
import { useMemo, useState } from 'react'
import Button from 'components/Button'
import {
  CollectiveProposalQueryResult,
  CollectiveTokenType,
  useProposalChangedSubscription,
  VotingAnonymity
} from 'graphql/generated'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useListProposalVotes } from 'hooks/collectives/useListProposalVotes'
import { useCollectiveDemoContext } from 'context/CollectiveDemoContext'
import { useCollectiveContext } from 'context/CollectiveContext'
import Typography from 'components/common/Typography'
import { useBreakpoint } from 'hooks/useBreakpoint'
import VoteListItem from './ProposalVoteListItem'

interface IProps {
  proposal: NonNullable<CollectiveProposalQueryResult['collectiveProposal']>
}
const headerContainerClasses =
  'flex flex-row justify-between cursor-pointer group/header'
const dismissContainerClasses =
  'w-[30px] rounded-xl flex justify-center items-center group-hover/header:bg-gray-100'
const noItemsContainerClasses = 'flex flex-row justify-center items-center'
const noItemsIconClasses = 'mr-[10px] text-[15px] text-gray-500'
const tableContainerClasses =
  'rounded-xl overflow-hidden border border-gray-200'

const ProposalVotesTable: React.FC<IProps> = ({ proposal }) => {
  const [show, setShow] = useState(false)
  const { demoMode, collective } = useCollectiveContext()
  const { edges, pageInfo, loadMore, loadingMore, refetch } =
    useListProposalVotes(proposal.id, demoMode)
  const { isSm } = useBreakpoint()

  useProposalChangedSubscription({
    variables: {
      proposalId: proposal.id
    },
    skip: demoMode,
    onData: () => refetch()
  })

  const { getVotesForProposalId } = useCollectiveDemoContext()

  const votes = useMemo(
    () =>
      demoMode
        ? getVotesForProposalId(proposal.id)?.edges.map(x => x.node)
        : edges?.map(x => x.node) || [],
    [demoMode, edges, getVotesForProposalId, proposal.id]
  )

  if (!votes) {
    return null
  }

  if (!votes.length) {
    return (
      <div className="bg-gray-50 p-[15px] rounded-[10px]">
        <div className={headerContainerClasses} onClick={() => setShow(!show)}>
          <Typography fontWeight="light" color="darkGray" className="pr-[7px]">
            {`Proposal Votes`}
          </Typography>
        </div>

        <div className={noItemsContainerClasses}>
          <FontAwesomeIcon
            icon={faFrown}
            size={'lg'}
            className={noItemsIconClasses}
          />
          <Typography color="gray" fontWeight="light" className="text-[15px]">
            {`No one voted on this proposal`}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 p-[15px] rounded-[10px]">
      <div className={headerContainerClasses} onClick={() => setShow(!show)}>
        <Typography fontWeight="light" color="darkGray" className="pr-[7px]">
          {`Proposal Votes`}
        </Typography>

        <div className={dismissContainerClasses}>
          <FontAwesomeIcon icon={show ? faChevronDown : faChevronRight} />
        </div>
      </div>

      {show && (
        <div className={'mt-5'}>
          <div className={tableContainerClasses}>
            <table className="divide-y divide-gray-200 w-full">
              {isSm && (
                <thead className={'bg-white'}>
                  <tr>
                    <th className="py-2.5 px-5 uppercase text-left text-gray-500 text-xs font-medium">
                      Voter
                    </th>
                    {proposal.votingAnonymity ===
                      VotingAnonymity.TRANSPARENT && (
                      <>
                        <th className="py-2.5 px-5 uppercase text-center text-gray-500 text-xs font-medium">
                          Vote
                        </th>
                        <th className="py-2.5 px-5 uppercase text-center text-gray-500 text-xs font-medium">
                          Weight
                        </th>
                      </>
                    )}
                    {proposal.requireVotingSignature && (
                      <th className="py-2.5 px-5 uppercase text-center text-gray-500 text-xs font-medium">
                        Signature
                      </th>
                    )}
                  </tr>
                </thead>
              )}

              <tbody className="bg-white divide-y divide-gray-200">
                {votes.map(vote => (
                  <VoteListItem
                    key={`vote-${vote.id}`}
                    vote={vote}
                    formatWeight={
                      collective.governanceType === CollectiveTokenType.OWNED
                    }
                    showVotes={
                      proposal.votingAnonymity === VotingAnonymity.TRANSPARENT
                    }
                    showSignature={proposal.requireVotingSignature}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {pageInfo?.hasNextPage && (
            <div className="flex flex-row justify-center items-center py-2">
              <Button
                label={loadingMore ? 'Loading...' : 'See More'}
                color={'white'}
                onClick={() => loadMore()}
                disabled={loadingMore}
              />
            </div>
          )}

          {loadingMore && (
            <div className="py-[10px] flex justify-center">
              <FontAwesomeIcon icon={faSpinner} spin size={'2x'} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function ProposalVotes({ proposal }: IProps) {
  if (proposal.votingAnonymity === VotingAnonymity.ANONYMOUS) {
    return (
      <div className="bg-gray-50 p-[15px] rounded-[10px]">
        <div className={headerContainerClasses}>
          <Typography fontWeight="light" color="darkGray" className="pr-[7px]">
            {`Proposal Votes`}
          </Typography>
        </div>

        <div className={noItemsContainerClasses}>
          <FontAwesomeIcon
            icon={faShield}
            size={'lg'}
            className={noItemsIconClasses}
          />
          <Typography color="gray" fontWeight="light" className="text-[15px]">
            {`This proposal's voting is anonymous`}
          </Typography>
        </div>
      </div>
    )
  }

  return <ProposalVotesTable proposal={proposal} />
}
